import _ from 'lodash';
import React from 'react';

import {
  useGenerateShippingLabelForTier1ReturnAuthorizationsMutation,
  usePrintLabelModalQuery,
  WeightUnit
} from 'generated/graphql';

import Big from 'big.js';
import * as types from './types';



export interface IReturnAuthorization {
  returnNumber: string;
  weightUnit: WeightUnit;
  weightValue: Big;
  sku: string;
  serialNumber: string;
  itemName: string;
}

export interface IData {
  id2Return: _.Dictionary<IReturnAuthorization>;
  formState: types.IFormState;
}

export function useQuery(returnAuthorizationGroupId: string) {
  const tuple = usePrintLabelModalQuery({
    variables: {
      returnAuthorizationGroupId
    }
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id2Return: _.mapValues(_.keyBy(tuple.data?.tier1ReturnAuthorizationGroup?.returnAuthorizations.nodes ?? [], node => node.id), (val): IReturnAuthorization => ({
          returnNumber: val.returnNumber,
          sku: val.itemReceipt?.item.sku ?? '',
          itemName: val.itemReceipt?.item.name ?? '',
          weightUnit: val.itemReceipt?.item.weightUnit ?? 'unknown',
          weightValue: new Big(val.itemReceipt?.item.weightValue ?? 0),
          serialNumber: val.itemReceipt?.serialNumber ?? ''
        })),
        formState: {
          weightOunces: '0',
          heightInches: '0',
          lengthInches: '0',
          widthInches: '0',
          returnAuthorizations: _.map(tuple.data?.tier1ReturnAuthorizationGroup?.returnAuthorizations.nodes ?? [], (node): types.IReturnAuthorization => ({
            isSelected: false,
            returnAuthorizationId: node.id
          })),

          shippingCity: '',
          shippingPostalCode: '',
          shippingStreet1: '',
          shippingStreet2: '',
          shippingUsStateAbbrev: 'AK',
          shippingCompany: '',
          shippingPhone: ''
        }
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useGenerateShippingLabelForTier1ReturnAuthorizations() {
  const [fnMutate, tuple] = useGenerateShippingLabelForTier1ReturnAuthorizationsMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState) => {
      const selectedReturns = _.filter(formState.returnAuthorizations, rma => rma.isSelected);
      if (_.isEmpty(selectedReturns)) {
        throw new Error('NO_RETURNS_SELECTED');
      }

      await fnMutate({
        variables: {
          input: {
            returnAuthorizationIds: selectedReturns.filter(r => r.isSelected).map(r => r.returnAuthorizationId),
            cartonDimensions: {
              height: {
                unit: 'inches',
                value: _.toNumber(formState.heightInches)
              },
              length: {
                unit: 'inches',
                value: _.toNumber(formState.lengthInches)
              },
              width: {
                unit: 'inches',
                value: _.toNumber(formState.widthInches)
              }
            },
            cartonWeight: {
              unit: 'ounces',
              value: _.toNumber(formState.weightOunces)
            },
            shipToAddress: {
              street1: formState.shippingStreet1,
              street2: formState.shippingStreet2,
              street3: '',
              city: formState.shippingCity,
              postalCode: formState.shippingPostalCode,
              countryCode: 'US',
              company: formState.shippingCompany,
              phone: formState.shippingPhone,
              usState: formState.shippingUsStateAbbrev
            }
          }
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: tuple.data?.generateShippingLabelForTier1ReturnAuthorizations ? {
          labelUrl: tuple.data.generateShippingLabelForTier1ReturnAuthorizations.shipment?.labelUrl ?? ''
        } : null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
